import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../helpers/language-context';
import { renderHTML } from '../../helpers/render-html';

const ServiceSection = () => {
    const { translations } = useLanguage();
    const [hoveredCard, setHoveredCard] = useState(null);
    const navigate = useNavigate();

    const services = [
        {
            id: 1,
            title: translations.services.mineralMining,
            description: translations.services.mineralMiningDesc,
            image: '/assets/images/services/mineral_mining.jpg'
        },
        {
            id: 2,
            title: translations.services.roadDevelopment,
            description: translations.services.roadDevelopmentDesc,
            image: '/assets/images/services/road_concession_equipment.png'
        },
        {
            id: 3,
            title: translations.services.quarryOperations,
            description: translations.services.quarryOperationsDesc,
            image: '/assets/images/services/quarry_operation.jpg'
        },
        {
            id: 4,
            title: translations.services.mineralWaterDevelopment,
            description: translations.services.mineralWaterDevelopmentDesc,
            image: '/assets/images/services/mineral_water_dev.jpg'
        },
        {
            id: 5,
            title: translations.services.consultancyServices,
            description: translations.services.consultancyServicesDesc,
            image: '/assets/images/services/asian_consultancy_services.png'
        },
        {
            id: 6,
            title: translations.services.smartDocketSystem,
            description: translations.services.smartDocketSystemDesc,
            image: '/assets/images/services/smart_docket_new.jpg'
        },
        {
            id: 7,
            title: translations.services.roadAssessmentSystem,
            description: translations.services.roadAssessmentSystemDesc,
            image: '/assets/images/services/road_assessment_system.png'
        },
        {
            id: 8,
            title: translations.services.cctv,
            description: translations.services.cctvDesc,
            image: '/assets/images/services/cctv_system.png'
        }
    ];

    return (
        <section className="bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
            <div className="mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-12">
                    <h1 className="text-3xl md:text-5xl text-[#28357B] leading-tight mb-4 sm:mb-0" dangerouslySetInnerHTML={renderHTML(translations.services.aCloserLookAtOurServices)} />
                    <button className="bg-[#28357B] hover:bg-[#28357B]/90 text-white px-6 py-3 rounded-full flex items-center gap-2 transition-all" onClick={() => navigate('/services')}>
                        {translations.button.exploreMore}
                        <svg
                            className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                            fill="none"
                            viewBox="0 0 14 10"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                                d="M1 5h12m0 0L9 1m4 4L9 9"
                            />
                        </svg>
                    </button>
                </div>

                {/* Cards Container */}
                <div className="flex flex-col gap-6">
                    {Array.from({ length: Math.ceil(services.length / 4) }, (_, rowIndex) => (
                        <div key={rowIndex} className="flex flex-col lg:flex-row gap-6 h-auto lg:h-[400px]">
                            {services.slice(rowIndex * 4, rowIndex * 4 + 4).map((service, colIndex) => {
                                const isHovered = hoveredCard === service.id;
                                const isHoverInThisRow = services.slice(rowIndex * 4, rowIndex * 4 + 4).some(s => s.id === hoveredCard);

                                return (
                                    <div
                                        key={service.id}
                                        onMouseEnter={() => setHoveredCard(service.id)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                        className={`relative rounded-3xl overflow-hidden cursor-pointer h-[200px] sm:h-[250px] lg:h-full w-full transition-all duration-700 ease-out ${isHoverInThisRow
                                            ? isHovered
                                                ? 'lg:flex-[2]'
                                                : 'lg:flex-[1]'
                                            : 'lg:flex-1'
                                            }`}
                                    >
                                        {/* Background Image */}
                                        <div
                                            className="absolute inset-0 bg-cover bg-center transition-transform duration-700"
                                            style={{
                                                backgroundImage: `url(${service.image})`,
                                                transform: isHovered ? 'scale(1.1)' : 'scale(1)'
                                            }}
                                        />

                                        {/* Overlay */}
                                        <div className={`absolute inset-0 bg-gradient-to-t transition-all duration-500 ${isHovered
                                            ? 'from-black/80 via-black/50 to-transparent'
                                            : 'from-black/60 via-black/30 to-transparent'
                                            }`} />

                                        {/* Content */}
                                        <div className="relative h-full flex flex-col justify-end p-8">
                                            <h2 className={`text-white font-bold mb-2 transition-all duration-500 ${isHovered ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'
                                                }`}>
                                                {service.title}
                                            </h2>
                                            <p className={`text-white/90 text-sm transition-all duration-500 ${isHovered ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0'
                                                }`}>
                                                {service.description}
                                            </p>
                                        </div>

                                        {/* Hover Indicator */}
                                        <div className={`hidden absolute top-6 right-6 w-10 h-10 bg-white rounded-full flex items-center justify-center transition-all duration-500 ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                                            }`}>
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                viewBox="0 0 14 10"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="1.5"
                                                    d="M1 5h12m0 0L9 1m4 4L9 9"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ServiceSection;
